import type { ChatConversation, ChatParticipant } from "../../backend/api/chat";
import { participantName } from "../../backend/api/chat";

/** Short clock time for a message that arrived today. */
export function messageTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * Relative-ish stamp for a conversation list row.
 *
 * Today shows a time, this week a weekday, anything older a date. A list of
 * threads is scanned rather than read, so the most specific useful unit wins.
 */
export function conversationStamp(iso: string | null): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";

  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();
  if (sameDay) {
    return date.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
    });
  }
  const daysAgo = (now.getTime() - date.getTime()) / (24 * 60 * 60 * 1000);
  if (daysAgo < 7) {
    return date.toLocaleDateString(undefined, { weekday: "short" });
  }
  return date.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

/**
 * Relative stamp for a message inside an open thread.
 *
 * The thread is read continuously, so a message from a minute ago reads as
 * "2m ago" rather than a clock time; older than that, a clock time is what
 * actually answers "when", so it reappears, qualified by day where the day
 * separator alone would not (SCRUM-29).
 */
export function relativeMessageTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const now = new Date();
  const diffMinutes = Math.floor((now.getTime() - date.getTime()) / 60_000);

  if (diffMinutes < 1) return "Just now";
  if (diffMinutes < 60) return `${diffMinutes}m ago`;

  const time = messageTime(iso);
  if (date.toDateString() === now.toDateString()) {
    return `${Math.floor(diffMinutes / 60)}h ago`;
  }

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) {
    return `Yesterday ${time}`;
  }

  const daysAgo = diffMinutes / (60 * 24);
  if (daysAgo < 7) {
    return `${date.toLocaleDateString(undefined, { weekday: "short" })} ${time}`;
  }
  return `${date.toLocaleDateString(undefined, { day: "numeric", month: "short" })} ${time}`;
}

/** Day separator label used between message groups. */
export function messageDayLabel(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const now = new Date();
  if (date.toDateString() === now.toDateString()) return "Today";
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
  return date.toLocaleDateString(undefined, {
    day: "numeric",
    month: "long",
    year: date.getFullYear() === now.getFullYear() ? undefined : "numeric",
  });
}

export function initialsFor(participant: ChatParticipant): string {
  const first = participant.firstname?.trim()?.[0] || "";
  const last = participant.lastname?.trim()?.[0] || "";
  const initials = `${first}${last}`.toUpperCase();
  return initials || "?";
}

/** Preview line for a conversation row, including the withdrawn case. */
export function conversationPreview(
  conversation: ChatConversation,
  currentUserId: string | null | undefined,
): string {
  if (!conversation.last_message_at) return "No messages yet";
  if (conversation.last_message_preview === null) return "Message withdrawn";
  const mine = conversation.last_message_sender_id === currentUserId;
  return mine
    ? `You: ${conversation.last_message_preview}`
    : conversation.last_message_preview;
}

/** "Alex is typing", "Alex and Sam are typing", "Several people are typing". */
export function typingLabel(
  userIds: string[],
  participants: ChatParticipant[],
): string | null {
  if (!userIds.length) return null;
  const names = userIds
    .map((id) => participants.find((p) => p.user_id === id))
    .filter((p): p is ChatParticipant => Boolean(p))
    .map(participantName);

  if (!names.length) return "Someone is typing";
  if (names.length === 1) return `${names[0]} is typing`;
  if (names.length === 2) return `${names[0]} and ${names[1]} are typing`;
  return "Several people are typing";
}

/** Unread counts stop being useful as exact numbers past this point. */
export function unreadLabel(count: number): string {
  return count > 99 ? "99+" : String(count);
}
